// =============================================
// Simulation Login Page
// Kipikid — מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, Eye, EyeOff, AlertCircle, Activity } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { adminApi } from '../../services/api';

export default function SimulationLogin() {
  const navigate = useNavigate();
  const { setUser, setToken, setRefreshToken, setTokenExpiresAt } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = 'קיפיקיד — סימולציה';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#dc2626');
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('יש להזין שם משתמש וסיסמה');
      return;
    }

    setIsLoading(true);

    try {
      const result = await adminApi.adminLogin(username, password);

      if (result.success && result.data) {
        const authData = result.data;
        const serverRole = authData.user?.role || '';
        const allRoles: string[] = authData.user?.roles || [];

        // SystemAdmin, Admin, NetworkManager, CallCenter — all management roles can access simulation
        const allowedRoles = ['SystemAdmin', 'Admin', 'NetworkManager', 'CityManager', 'Inspector', 'CallCenter'];
        const hasAccess = allowedRoles.includes(serverRole) || allRoles.some((r: string) => allowedRoles.includes(r));

        if (!hasAccess) {
          setError(`הרשאה לא מתאימה (${serverRole}). רק מנהלי מערכת יכולים להיכנס.`);
          setIsLoading(false);
          return;
        }

        setUser({
          id: authData.user?.id?.toString() || '',
          email: username,
          name: `${authData.user?.firstName || ''} ${authData.user?.lastName || ''}`.trim(),
          firstName: authData.user?.firstName,
          lastName: authData.user?.lastName,
          phone: authData.user?.phone,
          role: 'admin',
          publicId: authData.user?.publicId,
          organizationPublicId: authData.user?.organizationPublicId,
          organizationName: authData.user?.organizationName,
          serverRole,
        });

        setToken(authData.accessToken);
        if (authData.refreshToken) setRefreshToken(authData.refreshToken);
        if (authData.expiresAt) setTokenExpiresAt(authData.expiresAt);

        // token stored via setToken into sessionStorage-backed Zustand store
        navigate('/simulation/dashboard');
      } else {
        setError(result.message || 'שם משתמש או סיסמה שגויים');
      }
    } catch (err: any) {
      // axios throws on 401/4xx — extract message from response
      const msg = err?.response?.data?.message || err?.message || 'שגיאה בהתחברות';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 44px',
    fontSize: '15px',
    border: '2px solid #374151',
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
    backgroundColor: '#111827',
    color: 'white',
    transition: 'border-color 0.2s',
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #374151 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: '#1f2937',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(220, 38, 38, 0.2)',
          width: '100%',
          maxWidth: '400px',
          padding: '40px 32px',
          border: '1px solid #374151',
        }}
      >
        {/* Logo & Title */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '20px',
            background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Activity size={40} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626', marginBottom: '4px' }}>
            Simulation Mode
          </h1>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            כניסה ללוח בקרת סימולציה
          </p>
        </div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 16px', backgroundColor: '#451a03',
              borderRadius: '10px', marginBottom: '20px',
            }}
          >
            <AlertCircle size={18} color="#ef4444" />
            <span style={{ fontSize: '14px', color: '#fca5a5' }}>{error}</span>
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ position: 'relative' }}>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)}
              placeholder="שם משתמש" style={inputStyle} disabled={isLoading} />
            <User size={18} color="#6b7280"
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }} />
          </div>

          <div style={{ position: 'relative' }}>
            <input type={showPassword ? 'text' : 'password'} value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="סיסמה" style={inputStyle} disabled={isLoading} />
            <Lock size={18} color="#6b7280"
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }} />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              }}>
              {showPassword ? <EyeOff size={18} color="#6b7280" /> : <Eye size={18} color="#6b7280" />}
            </button>
          </div>

          <motion.button type="submit" disabled={isLoading}
            style={{
              width: '100%', padding: '16px', marginTop: '8px', borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
              color: 'white', fontSize: '16px', fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? 'מתחבר...' : 'כניסה לסימולציה'}
          </motion.button>
        </form>

        <button onClick={() => navigate('/')}
          style={{
            width: '100%', marginTop: '20px', padding: '12px',
            backgroundColor: 'transparent', color: '#6b7280',
            border: 'none', fontSize: '14px', cursor: 'pointer',
          }}
        >
          חזרה לדף הבית
        </button>
      </motion.div>
    </div>
  );
}
